import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Pressable,
  Image,
} from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { shadows } from '../lib/styles';
import { ArrowRight, Building, UserCheck } from '../lib/icons';
import {
  validateAgentIdentifier,
  setPendingAgentIdentifier,
  type ReferringAgent,
} from '../lib/referringAgent';

// Public landing page for a real estate agent's referral link.
// Route: /agent/:slug
// Visiting this page caches the agent slug in sessionStorage so the user
// can be linked to the agent during the standard onboarding flow.
const AgentLanding = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState<ReferringAgent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setError('Invalid agent link');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await validateAgentIdentifier(slug);
      if (cancelled) return;
      if (result.valid) {
        setAgent(result.agent);
        setPendingAgentIdentifier(result.agent.slug);
      } else {
        setError(result.error || 'Agent not found');
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleCta = () => {
    if (!agent) return;
    setPendingAgentIdentifier(agent.slug);
    navigate(`/signup?agent=${encodeURIComponent(agent.slug)}`);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#000" />
      </View>
    );
  }

  if (error || !agent) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>Agent not found</Text>
        <Text style={styles.errorText}>
          {error || "This referral link isn't active. You can still sign up for HoneyDone below."}
        </Text>
        <Link to="/signup" style={styles.errorLink}>
          <Text style={styles.errorLinkText}>Continue to signup</Text>
        </Link>
      </View>
    );
  }

  const firstName = agent.fullName.split(' ')[0];

  return (
    <View style={styles.container}>
      <WebScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          <View style={styles.heroCard}>
            <View style={styles.heroBadge}>
              <UserCheck size={14} color="#0f766e" />
              <Text style={styles.heroBadgeText}>Referred by your real estate agent</Text>
            </View>

            <View style={styles.agentRow}>
              {agent.photoUrl ? (
                <Image source={{ uri: agent.photoUrl }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatar, styles.avatarFallback]}>
                  <Text style={styles.avatarFallbackText}>
                    {firstName.charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <View style={styles.agentInfo}>
                <Text style={styles.agentName}>{agent.fullName}</Text>
                {agent.brokerage ? (
                  <View style={styles.brokerageRow}>
                    <Building size={14} color="#6b7280" />
                    <Text style={styles.brokerageText}>{agent.brokerage}</Text>
                  </View>
                ) : null}
              </View>
            </View>

            <Text style={styles.heroTitle}>
              {firstName} wants to help you take care of your new home.
            </Text>
            <Text style={styles.heroSubtitle}>
              HoneyDone is your home management service: maintenance plans, trusted service
              providers, and a real human to message when something breaks.
            </Text>

            {agent.bio ? (
              <View style={styles.bioBox}>
                <Text style={styles.bioText}>{agent.bio}</Text>
              </View>
            ) : null}

            <Pressable
              onPress={handleCta}
              style={({ pressed }) => [styles.ctaButton, pressed && styles.ctaButtonPressed]}
            >
              <Text style={styles.ctaButtonText}>Get HoneyDone with {firstName}</Text>
              <ArrowRight size={18} color="#ffffff" />
            </Pressable>

            <Text style={styles.fineprint}>
              {firstName} will appear on your "Your Team" page so you can stay in touch after
              closing.
            </Text>
          </View>

          <View style={styles.featuresRow}>
            <FeatureBullet
              title="Maintenance you don't have to remember"
              text="A custom plan for your home, on a schedule that actually happens."
            />
            <FeatureBullet
              title="Trusted local pros"
              text="We coordinate with vetted service providers when something needs fixing."
            />
            <FeatureBullet
              title="One person to message"
              text="Your home manager handles the details so you don't have to chase quotes."
            />
          </View>

          <View style={styles.signinRow}>
            <Text style={styles.signinText}>Already have a HoneyDone account?</Text>
            <Link to="/login" style={styles.signinLink}>
              <Text style={styles.signinLinkText}>Sign in</Text>
            </Link>
          </View>
        </View>
      </WebScrollView>
    </View>
  );
};

const FeatureBullet = ({ title, text }: { title: string; text: string }) => (
  <View style={styles.featureBullet}>
    <Text style={styles.featureBulletTitle}>{title}</Text>
    <Text style={styles.featureBulletText}>{text}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? ('var(--app-height)' as any) : '100%',
    backgroundColor: '#f9fafb',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingVertical: 48,
    paddingHorizontal: 16,
  },
  content: {
    width: '100%',
    maxWidth: 720,
    alignSelf: 'center',
    gap: 24,
  },
  heroCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    gap: 20,
    ...(Platform.OS === 'web' && shadows.md),
  },
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#ccfbf1',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    alignSelf: 'flex-start',
  },
  heroBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#0f766e',
  },
  agentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#e5e7eb',
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    fontSize: 28,
    fontWeight: '600',
    color: '#374151',
  },
  agentInfo: {
    flex: 1,
    gap: 4,
  },
  agentName: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  brokerageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  brokerageText: {
    fontSize: 14,
    color: '#6b7280',
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: '700',
    color: '#111827',
    lineHeight: 32,
  },
  heroSubtitle: {
    fontSize: 16,
    color: '#4b5563',
    lineHeight: 24,
  },
  bioBox: {
    backgroundColor: '#f9fafb',
    borderRadius: 10,
    padding: 16,
  },
  bioText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  ctaButton: {
    backgroundColor: '#000000',
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  ctaButtonPressed: {
    opacity: 0.85,
  },
  ctaButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  fineprint: {
    fontSize: 12,
    color: '#9ca3af',
    textAlign: 'center',
  },
  featuresRow: {
    gap: 12,
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
    }),
  },
  featureBullet: {
    flex: 1,
    minWidth: 200,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#f3f4f6',
    borderRadius: 12,
    padding: 16,
    gap: 6,
  },
  featureBulletTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  featureBulletText: {
    fontSize: 13,
    color: '#6b7280',
  },
  signinRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
  },
  signinText: {
    fontSize: 14,
    color: '#6b7280',
  },
  signinLink: {
    textDecorationLine: 'none',
  },
  signinLinkText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    ...(Platform.OS === 'web' && { textDecorationLine: 'underline' }),
  },
  loadingContainer: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? ('var(--app-height)' as any) : '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorContainer: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? ('var(--app-height)' as any) : '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    gap: 12,
  },
  errorTitle: {
    fontSize: 22,
    fontWeight: '600',
    color: '#111827',
  },
  errorText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    maxWidth: 480,
  },
  errorLink: {
    marginTop: 8,
    textDecorationLine: 'none',
  },
  errorLinkText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    ...(Platform.OS === 'web' && { textDecorationLine: 'underline' }),
  },
});

export default AgentLanding;
